import React from 'react';
import { createRoot } from 'react-dom/client';

export type ComponentWithSelector = React.ComponentType<any> & {
  selector: string;
};

type IslandImporter = () => Promise<ComponentWithSelector>;
type IslandRegistry = Record<string, IslandImporter>;
type MountPolicy = 'immediate' | 'visible' | 'idle' | 'interaction';

const pendingElements = new WeakSet<Element>();
const scheduledElements = new WeakSet<Element>();
const loadedComponents = new WeakMap<IslandImporter, Promise<ComponentWithSelector>>();

export function loadComponentOnPageLoad(Component: ComponentWithSelector): void {
  runOnPageLoad(() => {
    loadComponent(Component);
  });
}

export function loadRegisteredComponentsOnPageLoad(registry: IslandRegistry): void {
  runOnPageLoad(() => {
    void loadRegisteredComponents(registry);
  });
}

function runOnPageLoad(callback: () => void): void {
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }

  callback();
}

export async function loadRegisteredComponents(registry: IslandRegistry): Promise<void> {
  const targetsByComponentName = new Map<string, Element[]>();

  document.querySelectorAll('[data-react-component]').forEach((item) => {
    const componentName = item.getAttribute('data-react-component');

    if (!componentName) {
      return;
    }

    const targets = targetsByComponentName.get(componentName) ?? [];
    targets.push(item);
    targetsByComponentName.set(componentName, targets);
  });

  await Promise.all(
    Array.from(targetsByComponentName).map(([componentName, targets]) =>
      loadRegisteredComponent(registry, componentName, targets),
    ),
  );
}

async function loadRegisteredComponent(
  registry: IslandRegistry,
  componentName: string,
  targets: Element[],
): Promise<void> {
  const importer = registry[componentName];

  if (!importer) {
    return;
  }

  const immediateTargets = targets.filter((item) => getMountPolicy(item) === 'immediate');
  if (immediateTargets.length > 0) {
    const Component = await loadIsland(importer);
    loadComponentElements(Component, immediateTargets);
  }

  targets
    .filter((item) => getMountPolicy(item) !== 'immediate')
    .forEach((item) => scheduleDeferredMount(importer, item));
}

function scheduleDeferredMount(importer: IslandImporter, item: Element): void {
  if (!canMount(item) || scheduledElements.has(item)) {
    return;
  }

  scheduledElements.add(item);

  const mount = async () => {
    if (!canMount(item)) {
      return;
    }

    const Component = await loadIsland(importer);
    loadComponentElements(Component, [item]);
  };

  switch (getMountPolicy(item)) {
    case 'visible':
      observeVisibleMount(item, mount);
      return;
    case 'idle':
      mountWhenIdle(mount);
      return;
    case 'interaction':
      mountOnInteraction(item, mount);
      return;
    case 'immediate':
      void mount();
      return;
  }
}

function observeVisibleMount(item: Element, mount: () => Promise<void>): void {
  if (!('IntersectionObserver' in window)) {
    mountWhenIdle(mount);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      observer.disconnect();
      void mount();
    },
    { rootMargin: '200px' },
  );

  observer.observe(item);
}

function mountWhenIdle(mount: () => Promise<void>): void {
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: () => void) => void;
  };

  if (idleWindow.requestIdleCallback) {
    idleWindow.requestIdleCallback(() => {
      void mount();
    });
    return;
  }

  window.setTimeout(() => {
    void mount();
  }, 1);
}

function mountOnInteraction(item: Element, mount: () => Promise<void>): void {
  const run = () => {
    item.removeEventListener('pointerenter', run);
    item.removeEventListener('focusin', run);
    item.removeEventListener('click', run);
    void mount();
  };

  item.addEventListener('pointerenter', run, { once: true });
  item.addEventListener('focusin', run, { once: true });
  item.addEventListener('click', run, { once: true });
}

function loadIsland(importer: IslandImporter): Promise<ComponentWithSelector> {
  const existing = loadedComponents.get(importer);
  if (existing) {
    return existing;
  }

  const component = importer();
  loadedComponents.set(importer, component);
  return component;
}

function getMountPolicy(item: Element): MountPolicy {
  const policy =
    item.getAttribute('data-react-mount-policy') ??
    item.getAttribute('data-mount-policy') ??
    'immediate';

  return isMountPolicy(policy) ? policy : 'immediate';
}

function isMountPolicy(policy: string): policy is MountPolicy {
  return (
    policy === 'immediate' || policy === 'visible' || policy === 'idle' || policy === 'interaction'
  );
}

function parseFetchedProps(text: string) {
  const cleanedText = text.replace(")]}',", '');
  const parsed = JSON.parse(cleanedText);

  return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
}

export function loadComponent(Component: ComponentWithSelector) {
  if (!Component.selector) {
    return;
  }

  loadComponentElements(Component, Array.from(document.querySelectorAll(Component.selector)));
}

function loadComponentElements(Component: ComponentWithSelector, elements: Element[]): void {
  elements.forEach((item) => {
    if (!canMount(item)) {
      return;
    }

    if (item.hasAttribute('data-get-props')) {
      const getProps: string = item.getAttribute('data-get-props')!;
      item.removeAttribute('data-get-props');
      pendingElements.add(item);

      fetch(getProps, { method: 'GET' })
        .then((response) => response.text())
        .then((text) => {
          return parseFetchedProps(text);
        })
        .then((props) => {
          props ??= {};
          const root = createRoot(item);
          root.render(<Component {...props} />);
          item.setAttribute('loaded', 'true');
        })
        .catch((error) => {
          console.error('Error fetching or parsing data', error);
          item.setAttribute('data-get-props', getProps);
        })
        .finally(() => {
          pendingElements.delete(item);
        });

      return;
    }

    const propsJson = item.getAttribute('data-props');
    item.removeAttribute('data-props');
    pendingElements.add(item);

    try {
      const props = propsJson ? JSON.parse(propsJson) : {};
      const root = createRoot(item);
      root.render(<Component {...props} />);
      item.setAttribute('loaded', 'true');
    } catch (error) {
      console.error('Error parsing component props', error);
      if (propsJson) {
        item.setAttribute('data-props', propsJson);
      }
    } finally {
      pendingElements.delete(item);
    }
  });
}

function canMount(item: Element): boolean {
  return (
    !item.hasAttribute('loaded') && !item.hasAttribute('disabled') && !pendingElements.has(item)
  );
}
